// Shared billing constants and helpers
export const CREDIT_MULTIPLIER = 100;

export const fmtCr = (usdAmount) => {
  if (usdAmount === null || usdAmount === undefined) return '0';
  return Math.round(usdAmount * CREDIT_MULTIPLIER).toLocaleString();
};

export const ACTION_LABELS = {
  brain: 'AI Reasoning (Deep)',
  sonnet: 'AI Reasoning',
  fast: 'AI Assist',
  onboarding: 'AI Onboarding',
  module: 'AI Module Analysis',
  supervisor: 'AI Supervisor',
  contact_reveal: 'Contact Reveal',
  apollo_reveal: 'Contact Reveal',
  apollo_mobile_reveal_premium: 'Mobile Number Reveal',
  lead_search: 'Lead Discovery',
  apollo_search: 'Lead Discovery',
  data_enrichment: 'Data Enrichment',
  apollo_enrichment: 'Data Enrichment',
  email_send: 'Email Sent',
  linkedin_action: 'LinkedIn Action',
  whatsapp_marketing: 'WhatsApp Marketing Message',
  whatsapp_utility: 'WhatsApp Notification',
  whatsapp_authentication: 'WhatsApp Verification Code',
  whatsapp_service: 'WhatsApp Service Reply',
  voice_call: 'Voice Call',
  voice_call_discovery: 'Voice: Discovery',
  voice_call_followup: 'Voice: Follow-up',
  voice_call_closing: 'Voice: Closing',
  voice_call_demo_scheduling: 'Voice: Demo Scheduling',
  voice_call_re_engagement: 'Voice: Re-engagement',
  outbound_call: 'Voice Call',
  voice_test_session: 'Voice Test',
  number_rental: 'Phone Number Rental',
  voicelink_number_rental: 'Phone Number Rental',
  workflow_action: 'Workflow Step',
  forecast: 'AI Forecast',
  credit_purchase: 'Credit Purchase',
  pool_commit: 'Prepaid Credit Pool',
  governor_grant: 'Account Credit',
  refund: 'Refund',
  overdraft_writeoff: 'Balance Adjustment',
  plan_credits_expired: 'Plan Credits Expired',
  migration_scoring: 'Lead Re-scoring',
  sync_conflict: 'Data Sync',
};

// Last-resort guard so a data-vendor name never surfaces in the UI.
const VENDOR_WORDS = ['apollo', 'sonnet', 'opus', 'claude', 'gpt', 'explee', 'elevenlabs'];
const scrubVendor = (text) => {
  const low = (text || '').toLowerCase();
  if (!VENDOR_WORDS.some(w => low.includes(w))) return text;
  let out = text;
  VENDOR_WORDS.forEach(w => {
    out = out.replace(new RegExp(w, 'gi'), '');
  });
  out = out.replace(/\s+/g, ' ').trim();
  return out || 'Service';
};

export const HIDDEN_ACTIONS = new Set([
  'voice_call_byok', 'voice_call_byok_discovery', 'voice_call_byok_demo_scheduling',
  'voice_call_byok_followup', 'voice_call_byok_closing', 'voice_call_byok_re_engagement',
  'crm_sync', 'territory_routing', 'sla_check',
]);

/**
 * Currency-aware money formatter. USD amounts are the source of truth across the
 * app; for Indian (Razorpay/INR) buyers we convert to ₹ using the live usd_inr_rate
 * returned by /gateway-config. Falls back to USD when no rate is available.
 */
export const formatMoney = (usdAmount, { currency = 'USD', usdInrRate = 0 } = {}) => {
  if (usdAmount === null || usdAmount === undefined) return '—';
  if (currency === 'INR' && usdInrRate) {
    return `₹${Math.round(usdAmount * usdInrRate).toLocaleString('en-IN')}`;
  }
  return `$${usdAmount}`;
};

export const getActionLabel = (action) => ACTION_LABELS[action] || scrubVendor((action || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));
export const isVisibleAction = (action) => !HIDDEN_ACTIONS.has(action);

export const getActionIcon = (action) => {
  if (action === 'credit_purchase') return 'text-green-500';
  if (action.includes('voice')) return 'text-emerald-500';
  if (['brain', 'fast', 'onboarding', 'module', 'supervisor', 'forecast'].includes(action)) return 'text-blue-500';
  if (['contact_reveal', 'apollo_reveal', 'lead_search', 'data_enrichment'].includes(action)) return 'text-amber-500';
  return 'text-purple-500';
};

export const getPlanIcon = (planId, icons) => {
  switch (planId) {
    case 'free': return icons.Zap;
    case 'starter': return icons.Rocket;
    case 'growth': return icons.TrendingUp;
    case 'scale': return icons.Star;
    case 'enterprise': return icons.Building2;
    default: return icons.Star;
  }
};

export const getPlanColor = (planId) => {
  switch (planId) {
    case 'free': return 'text-slate-400';
    case 'starter': return 'text-blue-400';
    case 'growth': return 'text-amber-400';
    case 'scale': return 'text-emerald-400';
    case 'enterprise': return 'text-purple-400';
    default: return 'text-slate-400';
  }
};

export const getStatusBadge = (status, Badge) => {
  switch (status) {
    case 'active':
      return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Active</Badge>;
    case 'past_due':
      return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Past Due</Badge>;
    case 'cancelled':
      return <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30">Cancelled</Badge>;
    case 'suspended':
      return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Suspended</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

export const getLiveEligibilityBadge = (plan, Badge) => {
  if (!plan) return null;
  if (plan.live_mode_allowed === true) {
    return <Badge variant="outline" className="text-green-400 border-green-500/30">LIVE Allowed</Badge>;
  } else if (plan.custom === true) {
    return <Badge variant="outline" className="text-amber-400 border-amber-500/30">Custom</Badge>;
  } else {
    return <Badge variant="outline" className="text-slate-400 border-slate-500/30">SIMULATED Only</Badge>;
  }
};

/**
 * Detect if a plan is a custom/contact-sales plan (no self-serve Stripe flow).
 * Prefer this over hardcoded plan_id checks so new custom plans work without code changes.
 */
export const isCustomPlan = (plan) => {
  if (!plan) return false;
  return plan.custom === true || plan.monthly_price_usd === null || plan.monthly_price_usd === undefined;
};
